import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import React from 'react';
import { Unit } from '../apiTypes';
import { t } from '../constants/translations';
import { useLocale } from '../contexts/locale.context';

interface Props {
  ingredient: {
    unit: Unit;
    qpp?: number | null;
  };
  qnt: number;
  onChange: (qnt: number) => void;
  autoFocus?: boolean;
  disablePacking?: boolean;
  helperText?: string[];
  availableQnt?: number;
  canBeNegative?: boolean;
  disabled?: boolean;
  name?: string;
}

const QntInput = (props: Props) => {
  const { lang } = useLocale();
  const {
    qnt,
    ingredient,
    onChange,
    availableQnt,
    helperText = [],
    disablePacking,
    autoFocus,
    canBeNegative,
    disabled,
    name,
  } = props;
  const { unit, qpp } = ingredient;

  const handleChange = (newQnt: number) => {
    if (!canBeNegative && newQnt < 0) {
      onChange(0);
      return;
    }
    if ((availableQnt !== undefined && availableQnt > 0 && availableQnt >= newQnt) || availableQnt === undefined) {
      onChange(Math.round(newQnt));
    }
  };

  return (
    <>
      {!canBeNegative && !disablePacking && (qpp || unit === 'g' || unit === 'ml') ? (
        <Box display="flex" alignItems="center" gap={1} width="100%" minWidth="130px">
          <TextField
            fullWidth
            name={`${name}.pack`}
            variant="outlined"
            type="number"
            size="small"
            color="secondary"
            disabled={disabled}
            value={String(Math.floor(qnt / (qpp || 1000)))}
            onChange={(e) => handleChange(Number(e.target.value) * (qpp || 1000) + (qnt % (qpp || 1000)))}
            autoFocus={autoFocus}
            InputProps={{
              sx: { bgcolor: (t) => t.palette.background.paper },
              endAdornment: (
                <InputAdornment position="start">
                  {qpp ? t.pac[lang] : `${unit === 'g' ? t.kg[lang] : t.l[lang]}`}
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            variant="outlined"
            type="number"
            size="small"
            color="secondary"
            name={`${name}.unit`}
            value={String(qnt % (qpp || 1000))}
            disabled={disabled}
            onChange={(e) =>
              Number(e.target.value) < (qpp || 1000) &&
              handleChange(Math.floor(qnt / (qpp || 1000)) * (qpp || 1000) + Number(e.target.value))
            }
            InputProps={{
              sx: { bgcolor: (t) => t.palette.background.paper },
              endAdornment: <InputAdornment position="start">{t[unit][lang]}</InputAdornment>,
            }}
          />
        </Box>
      ) : (
        <TextField
          fullWidth
          variant="outlined"
          type="number"
          size="small"
          defaultValue={String(qnt)}
          disabled={disabled}
          autoFocus={autoFocus}
          name={name}
          onChange={(e) => {
            if (e.target.value !== '-') {
              handleChange(Number(e.target.value));
            }
          }}
          InputProps={{
            sx: { bgcolor: (t) => t.palette.background.paper, pr: 1 },
            endAdornment: <InputAdornment position="start">{t[unit][lang]}</InputAdornment>,
          }}
        />
      )}

      {!!helperText.length && (
        <div>
          {helperText.map((text) => (
            <Typography variant="caption" color="textSecondary" sx={{ marginRight: 2 }} key={text}>
              {text}
            </Typography>
          ))}
        </div>
      )}
    </>
  );
};

export default QntInput;
